import React, { useState, useEffect, useCallback } from 'react';

/**
 * Translation Page Settings tab.
 * ──────────────────────────────
 * Edits the page_title + every user-visible label on /translation. Writes to
 * the new `translation_page_settings` collection. The backend also mirrors
 * page_title into the existing SEO Meta Tags Manager doc for /translation
 * (one-way write — SEO admin code itself isn't touched).
 *
 * Field groups below mirror the DEFAULT_LABELS keys in
 * backend/routes/translation_page_settings_routes.py — keep them in sync.
 */

const FIELD_GROUPS = [
  {
    heading: '🏷️ Page Title (tab + SEO)',
    description: 'Shown in browser tab. Also mirrored into the SEO Meta Tags Manager for /translation.',
    fields: [
      { key: 'page_title', label: 'Page Title', placeholder: 'Translate English ↔ Darija ↔ Arabic | Darija Pro', isTopLevel: true },
    ],
  },
  // NOTE: '⏰ Daily Limit Modal' group was intentionally removed from the
  // admin UI because the modal it controls is dead in the live pipeline —
  // free users are blocked at the subscription gate before the limit modal
  // ever fires. The label keys (daily_limit_reached_title, upgrade_cta_*,
  // come_back_tomorrow_button, limit_reset_info, todays_usage_label,
  // current_plan_label) still exist in backend DEFAULT_LABELS so the modal
  // JSX in TranslationInterface.js (also dead) keeps resolving to a string.
  // DO NOT re-add this group without first re-enabling the daily-limit
  // feature for paid users.
  {
    heading: '🔒 Pro / Login Gates',
    description: 'Tooltips and notifications shown when guests or free-tier users hit Pro-only features.',
    fields: [
      { key: 'voice_locked_guest', label: 'Voice (guest tooltip)' },
      { key: 'voice_locked_user', label: 'Voice (logged-in tooltip)' },
      { key: 'voice_locked_notification', label: 'Voice (notification)' },
      { key: 'favorites_locked_guest', label: 'Favorites (guest tooltip)' },
      { key: 'favorites_locked_user', label: 'Favorites (logged-in tooltip)' },
      { key: 'favorites_locked_notification', label: 'Favorites (notification)' },
      { key: 'audio_locked_guest', label: 'Audio (guest tooltip)' },
      { key: 'audio_locked_user', label: 'Audio (logged-in tooltip)' },
      { key: 'audio_locked_notification', label: 'Audio (notification)' },
      { key: 'word_bank_locked', label: 'Word Bank (notification)' },
    ],
  },
  {
    heading: '💬 Notifications',
    description: 'Short toast messages shown after user actions.',
    fields: [
      { key: 'added_to_favorites', label: 'Added to favorites' },
      { key: 'removed_from_favorites', label: 'Removed from favorites' },
      { key: 'add_to_favorites_tooltip', label: 'Add to favorites (tooltip)' },
      { key: 'audio_not_supported', label: 'Audio not supported' },
      { key: 'audio_playback_failed', label: 'Audio playback failed' },
      { key: 'copy_success', label: 'Copy success' },
      { key: 'copy_failed', label: 'Copy failed' },
      { key: 'guest_empty_state_hint', label: 'Guest empty-state hint' },
    ],
  },
];

const TranslationPageSettingsTab = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [pageTitle, setPageTitle] = useState('');
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/translation/page-settings`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setPageTitle(d.page_title || '');
      setLabels(d.labels || {});
    } catch (e) {
      showNotification?.(`Failed to load Page Settings: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [API, adminToken, showNotification]);

  useEffect(() => { load(); }, [load]);

  const setLabel = (key, value) => setLabels((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    try {
      setSaving(true);
      const r = await fetch(`${API}/admin/translation/page-settings`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_title: pageTitle, labels }),
      });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setPageTitle(d.page_title || '');
      setLabels(d.labels || {});
      showNotification?.('Page Settings saved (mirrored to SEO Meta Tags Manager)', 'success');
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={`p-6 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading Page Settings…</div>;
  }

  const cardCls = `p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const labelCls = `block text-xs font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`;
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const headingCls = `text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`;
  const subtitleCls = `text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`;

  return (
    <div className="space-y-5 max-w-6xl">
      {/* Green header + Save button stays at the top, full width. */}
      <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${isDarkMode ? 'bg-emerald-900/20 border-emerald-700' : 'bg-emerald-50 border-emerald-300'}`}>
        <div>
          <div className={`font-bold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-900'}`}>🏷️ Translation Page Settings</div>
          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
            Single source of truth for every label on /translation. Title is also mirrored into SEO Meta Tags Manager.
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold shadow disabled:opacity-50"
        >
          {saving ? 'Saving…' : '💾 Save'}
        </button>
      </div>

      {/* Two-column layout on desktop: form on the left (col-span-2), red
          sync-direction warning on the right (sticky, stays in view while
          editing). On mobile (single col), the warning shows FIRST above the
          form via `order-1 lg:order-2` reordering. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Form cards — left side on desktop (2 of 3 cols), second on mobile */}
        <div className="lg:col-span-2 space-y-5 order-2 lg:order-1">
          {FIELD_GROUPS.map((group) => (
            <div key={group.heading} className={cardCls}>
              <div className={headingCls}>{group.heading}</div>
              <div className={subtitleCls}>{group.description}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {group.fields.map((f) => {
                  const value = f.isTopLevel ? pageTitle : (labels[f.key] ?? '');
                  const onChange = (v) => f.isTopLevel ? setPageTitle(v) : setLabel(f.key, v);
                  return (
                    <div key={f.key} className={f.isTopLevel ? 'md:col-span-2' : ''}>
                      <label className={labelCls}>{f.label}</label>
                      <input
                        type="text"
                        className={inputCls}
                        value={value}
                        placeholder={f.placeholder || ''}
                        onChange={(e) => onChange(e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Red sync-direction warning — right side on desktop (sticky, stays
            in viewport while the form scrolls), first on mobile. DO NOT REMOVE. */}
        <div className="order-1 lg:order-2 lg:sticky lg:top-4 lg:self-start">
          <div className={`p-4 rounded-xl border-2 ${isDarkMode ? 'bg-red-900/30 border-red-600' : 'bg-red-50 border-red-400'}`}>
            <div className={`font-bold text-base mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
              ⚠️ How the sync direction works — READ BEFORE EDITING
            </div>
            <div className={`text-sm space-y-2 ${isDarkMode ? 'text-red-200' : 'text-red-900'}`}>
              <div>
                <strong>Translation page settings is the SOURCE.</strong> When you change the Page Title
                here and click Save, the backend ALSO writes it into both <code>page_title</code> AND
                <code> meta_title</code> on the existing SEO &gt; Meta Tags Manager doc for <code>/translation</code>.
                One value, two views — they will always match after a save here.
              </div>
              <div>
                <strong>Do NOT edit the title directly in SEO &gt; Meta Tags Manager for /translation.</strong>
                That change won't flow back here, and the next save on this page will overwrite it.
                Other SEO fields (description, og:*, twitter:*, schema markup, robots, canonical) ARE
                owned by SEO Meta Tags Manager — edit those there, they won't be touched by this tab.
              </div>
              <div>
                <strong>All labels below are read by the public Translation page from the API.</strong>
                Saving here invalidates the SSR cache so the next visit to <code>/translation</code> sees the
                new title baked into the HTML — no React flicker.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold shadow disabled:opacity-50"
        >
          {saving ? 'Saving…' : '💾 Save'}
        </button>
      </div>
    </div>
  );
};

export default TranslationPageSettingsTab;
